// Company Prospect Research management functions
// This is a shared resource accessible to all users
import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  getDocs,
  query,
  orderBy,
  serverTimestamp,
  onSnapshot
} from 'firebase/firestore'
import { db } from './config'

const COMPANY_PROSPECTS_COLLECTION = 'companyProspects'

/**
 * Create a new company prospect
 * @param {Object} companyData - Company data (name, industry, employeeSize, country, website, tags)
 * @returns {Promise<string>} Document ID
 */
export const createCompanyProspect = async (companyData) => {
  try {
    const docRef = await addDoc(collection(db, COMPANY_PROSPECTS_COLLECTION), {
      ...companyData,
      tags: companyData.tags || [],
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    })
    return docRef.id
  } catch (error) {
    console.error('Error creating company prospect:', error)
    throw error
  }
}

/**
 * Update an existing company prospect
 * @param {string} companyId - Company prospect document ID
 * @param {Object} updates - Fields to update
 */
export const updateCompanyProspect = async (companyId, updates) => {
  try {
    const docRef = doc(db, COMPANY_PROSPECTS_COLLECTION, companyId)
    await updateDoc(docRef, {
      ...updates,
      updatedAt: serverTimestamp()
    })
  } catch (error) {
    console.error('Error updating company prospect:', error)
    throw error
  }
}

/**
 * Delete a company prospect
 * @param {string} companyId - Company prospect document ID
 */
export const deleteCompanyProspect = async (companyId) => {
  try {
    const companyRef = doc(db, COMPANY_PROSPECTS_COLLECTION, companyId)
    await deleteDoc(companyRef)
  } catch (error) {
    console.error('Error deleting company prospect:', error)
    throw error
  }
}

/**
 * Get all company prospects
 * @returns {Promise<Array>} Array of company prospects
 */
export const getCompanyProspects = async () => {
  try {
    const q = query(
      collection(db, COMPANY_PROSPECTS_COLLECTION),
      orderBy('createdAt', 'desc')
    )
    
    const querySnapshot = await getDocs(q)
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }))
  } catch (error) {
    console.error('Error getting company prospects:', error)
    throw error
  }
}

/**
 * Subscribe to company prospects changes (real-time)
 * This is a shared resource, so no user filtering
 * @param {Function} callback - Callback function
 * @returns {Function} Unsubscribe function
 */
export const subscribeToCompanyProspects = (callback) => {
  const q = query(
    collection(db, COMPANY_PROSPECTS_COLLECTION),
    orderBy('createdAt', 'desc')
  )
  
  return onSnapshot(q, (snapshot) => {
    const companies = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }))
    callback(companies)
  }, (error) => {
    console.error('Error subscribing to company prospects:', error)
  })
}
